<script setup lang="ts">
  import { useGetAccountStates } from "@/api/account-state/account-state.ts"
  import {
    useGetAccount,
    useGetMetersByBillingAccountId,
  } from "@/api/account/account.ts"
  import { useGetFinalBill } from "@/api/bill/bill.ts"
  import { BillGenerationState, DirectDebitState } from "@/api/model"
  import {
    useGetLatestDirectDebit,
    useGetPaymentScheduleDetailsV2,
    useGetVariablePaymentDetailsV2,
  } from "@/api/payments/payments.ts"
  import { activeDirectDebitStates } from "@/constants/directDebitStates.ts"
  import { ExternalRoute } from "@/constants/externalRoute.ts"
  import {
    leavingSupplyStates,
    moverSupplyStates,
    switchedAwaySupplyStates,
  } from "@/constants/leavingSupplyStates.ts"
  import { MyAccountRoute } from "@/constants/routes/myAccount.ts"
  import { activeSwitchStates } from "@/constants/switchStates.ts" // Add this import
  import { aggregateQueries } from "@/lib/aggregateQueries.ts"
  import { formatDate } from "@/lib/dateHelper.ts"
  import { isSummerMonth } from "@/lib/seasonalHelper.ts"
  import { useGetAccountId } from "@/lib/useGetAccountId.ts"
  import { formatCurrency } from "@/lib/utils.ts"
  import dayjs from "dayjs"
  import { computed } from "vue"
  import ErrorMessage from "@/components/ErrorMessage.vue"
  import { Button, ButtonVariants } from "@/components/ui/button"
  import {
    Card,
    CardContent,
    CardFooter,
    CardHeader,
    CardTitle,
  } from "@/components/ui/card"
  import { Link } from "@/components/ui/link"

  import { useMoveOutTimeline } from "@/views/myaccount/home/components/move-out-timeline/useMoveOutTimeline.ts"

  import DirectDebitCardSkeleton from "./DirectDebitCardSkeleton.vue"

  const accountIdQuery = useGetAccountId()
  const accountId = accountIdQuery.accountId

  const accountStatesQuery = useGetAccountStates(accountId)
  const metersQuery = useGetMetersByBillingAccountId(accountId)

  const directDebitState = computed(
    () => accountStatesQuery.data.value?.directDebit,
  )

  const isCancelled = computed(
    () => directDebitState.value === DirectDebitState.cancelled,
  )

  const isLeavingWithoutDirectDebit = computed(() => {
    const supply = accountStatesQuery.data.value?.supply
    return (
      !!supply &&
      leavingSupplyStates.has(supply) &&
      (isCancelled.value ||
        directDebitState.value === DirectDebitState.noPriorDirectDebit)
    )
  })

  const accountQuery = useGetAccount(accountId)
  const latestDirectDebitQuery = useGetLatestDirectDebit(accountId, {
    query: {
      enabled: computed(
        () =>
          !!accountId.value &&
          (isLeavingWithoutDirectDebit.value || isCancelled.value),
      ),
    },
  })
  const finalBillQuery = useGetFinalBill(accountId, {
    query: {
      enabled: computed(
        () => !!accountId.value && isLeavingWithoutDirectDebit.value,
      ),
    },
  })

  // From their last day of responsibility (movers) or once the switch is complete (switchers), a customer with
  // an active Direct Debit can no longer adjust it from the card
  const moveOutTimeline = useMoveOutTimeline()
  const isActiveDirectDebit = computed(
    () =>
      !!directDebitState.value &&
      activeDirectDebitStates.has(directDebitState.value),
  )
  const hasLeftUs = computed(() => {
    const supply = accountStatesQuery.data.value?.supply
    return (
      moveOutTimeline.isMovingOut.value ||
      (!!supply && switchedAwaySupplyStates.has(supply))
    )
  })
  const isWaitingForMoveOutTimeline = computed(() => {
    const supply = accountStatesQuery.data.value?.supply
    return (
      isActiveDirectDebit.value &&
      !!supply &&
      moverSupplyStates.has(supply) &&
      moveOutTimeline.isLoading.value
    )
  })

  // The BE returns a positive balance for debit and a negative one for credit
  const finalBillToPay = computed(() => {
    const balance = accountQuery.data.value?.balance ?? 0
    return balance > 0 ? finalBillQuery.data.value?.finalBill : undefined
  })

  const paymentScheduleDetailsQuery = useGetPaymentScheduleDetailsV2(
    accountId,
    {
      query: {
        enabled: computed(
          () => !!accountId.value && isDDEligible(directDebitState.value),
        ),
      },
    },
  )
  const paymentSchedule = paymentScheduleDetailsQuery.data

  const ddVariableNextPaymentDateQuery = useGetVariablePaymentDetailsV2(
    accountId,
    {
      query: {
        enabled: computed(
          () =>
            !!accountId.value &&
            directDebitState.value === DirectDebitState.activeVariable,
        ),
      },
    },
  )
  const ddVariableNextPaymentDate = ddVariableNextPaymentDateQuery.data

  const { isError, isFetching, isLoading } = aggregateQueries([
    accountIdQuery,
    accountStatesQuery,
    accountQuery,
    paymentScheduleDetailsQuery,
    ddVariableNextPaymentDateQuery,
    latestDirectDebitQuery,
    finalBillQuery,
  ])

  function isDDEligible(ddState?: DirectDebitState) {
    return (
      ddState === DirectDebitState.activeFixed ||
      ddState === DirectDebitState.pendingActivation ||
      ddState === DirectDebitState.failedPayment
    )
  }

  const noDirectDebitText = computed(() => {
    if (!isCancelled.value) {
      return "Not set up"
    }
    const cancelledOn = latestDirectDebitQuery.data.value?.cancelledOn
    return cancelledOn
      ? `Cancelled ${formatDate(cancelledOn, "long")}`
      : "Cancelled"
  })

  const paymentAmountText = computed(() => {
    if (isLeavingWithoutDirectDebit.value || isCancelled.value) {
      return noDirectDebitText.value
    }

    if (directDebitState.value === DirectDebitState.activeVariable) {
      return "On receipt of bill"
    }

    if (!isDDEligible(directDebitState.value)) {
      return "Not set up"
    }

    const schedule = paymentSchedule.value?.currentSchedule || {}
    const {
      nextPaymentAmountInPounds,
      nextPaymentDate,
      isSeasonal,
      paymentAmount,
    } = schedule

    if (!nextPaymentAmountInPounds) return "-"

    if (
      isSeasonal &&
      paymentAmount?.summerPaymentAmountInPounds &&
      paymentAmount?.winterPaymentAmountInPounds
    ) {
      const seasonalAmount = isSummerMonth(dayjs(nextPaymentDate))
        ? paymentAmount.summerPaymentAmountInPounds
        : paymentAmount.winterPaymentAmountInPounds
      return formatCurrency(seasonalAmount)
    }

    return formatCurrency(nextPaymentAmountInPounds)
  })

  const directDebitTitleCopy = computed(() => {
    return paymentSchedule.value?.currentSchedule?.isSeasonal
      ? "Your next Direct Debit"
      : "Your Direct Debit"
  })

  const getDiffText = (diffInDays: number) => {
    switch (diffInDays) {
      case -1:
        return "(1 day overdue)"
      case 0:
        return "(today)"
      case 1:
        return "(in 1 day)"
      default:
        return diffInDays < 0
          ? `(${-diffInDays} days overdue)`
          : `(in ${diffInDays} days)`
    }
  }

  const formatNextPaymentDate = (nextPaymentDate: string) => {
    const targetDate = dayjs(nextPaymentDate)

    return {
      date: targetDate.format("D MMM"),
      diffInDays: getDiffText(
        targetDate.diff(dayjs().format("DD MMM YYYY"), "day"),
      ),
    }
  }

  const nextPaymentDate = computed(() => {
    if (isLeavingWithoutDirectDebit.value) {
      const dueOn = finalBillToPay.value?.dueOn
      return dueOn ? formatNextPaymentDate(dueOn) : null
    }

    if (directDebitState.value === DirectDebitState.pendingActivation) {
      return {
        date: "Pending activation",
        diffInDays: "",
      }
    }

    if (
      !directDebitState.value ||
      !activeDirectDebitStates.has(directDebitState.value)
    ) {
      return null
    }

    const nextPaymentDate =
      directDebitState.value === DirectDebitState.activeVariable
        ? ddVariableNextPaymentDate.value?.nextPaymentDate
        : paymentSchedule.value?.currentSchedule?.nextPaymentDate

    if (!nextPaymentDate) return null

    return formatNextPaymentDate(nextPaymentDate)
  })

  const expectedFirstPaymentText = computed(() => {
    const state = accountStatesQuery.data.value
    const meters = metersQuery.data.value || []

    if (
      !state?.supply ||
      !activeSwitchStates.has(state.supply) ||
      state.billGeneration !== BillGenerationState.noBillNewAccount
    )
      return

    const earliestMeter = meters
      .filter((meter) => meter.switchStartDate && meter.supplyStartDate)
      .sort((a, b) =>
        dayjs(a.switchStartDate).diff(dayjs(b.switchStartDate)),
      )[0]

    if (!earliestMeter) return

    const startDate = dayjs(earliestMeter.switchStartDate)
    const endDate = dayjs(earliestMeter.supplyStartDate).add(10, "day")

    if (dayjs().isAfter(endDate)) return
    return `Expected ${formatDate(startDate, "dayMonthShort")}-${formatDate(endDate, "dayMonthShort")}`
  })

  const actionButton = computed<
    | {
        text: string
        variant: ButtonVariants["variant"]
        to: string
      }
    | undefined
  >(() => {
    if (isLeavingWithoutDirectDebit.value) {
      return finalBillToPay.value
        ? {
            text: "Pay your final bill",
            variant: "primary",
            to: MyAccountRoute.cardPayment,
          }
        : undefined
    }

    if (isActiveDirectDebit.value) {
      return hasLeftUs.value
        ? undefined
        : {
            text: "Adjust your Direct Debit",
            variant: "tonal",
            to: ExternalRoute.payments,
          }
    }

    return {
      text: "Set up your Direct Debit",
      variant: "primary",
      to: ExternalRoute.payments,
    }
  })
</script>

<template>
  <DirectDebitCardSkeleton v-if="isLoading || isWaitingForMoveOutTimeline" />
  <Card v-else data-testid="direct-debit-card">
    <CardHeader>
      <CardTitle>{{ directDebitTitleCopy }}</CardTitle>
    </CardHeader>
    <CardContent>
      <ErrorMessage v-if="isError && !isFetching" />
      <dl v-if="!isError" class="grid grid-cols-[1fr_max-content] gap-2">
        <dt>{{ directDebitTitleCopy }}:</dt>
        <dd class="text-right font-medium" data-testid="next-payment-amount">
          {{ paymentAmountText }}
        </dd>
        <template v-if="expectedFirstPaymentText">
          <dt>First payment:</dt>
          <dd
            class="text-grey-dark text-right text-sm font-normal"
            data-testid="first-payment-date"
          >
            <span>
              {{ expectedFirstPaymentText }}
            </span>
          </dd>
        </template>
        <dt>Next payment due:</dt>
        <dd class="text-right font-medium" data-testid="next-payment-date">
          <span v-if="nextPaymentDate">
            {{ nextPaymentDate.date }}
            <span v-if="nextPaymentDate.diffInDays" class="font-normal">
              {{ nextPaymentDate.diffInDays }}
            </span>
          </span>
          <span v-else>-</span>
        </dd>
      </dl>
    </CardContent>
    <CardFooter v-if="actionButton">
      <Button
        :variant="actionButton.variant"
        :disabled="isFetching"
        :as-child="!isFetching"
        data-testid="go-to-payments-button"
      >
        <span v-if="isFetching">{{ actionButton.text }}</span>
        <Link v-else :to="actionButton.to">
          {{ actionButton.text }}
        </Link>
      </Button>
    </CardFooter>
  </Card>
</template>
